import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import GroupIcon from "@material-ui/icons/Group";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import CreateGroupModal from "../../components/CreateGroupModal";
import EditGroupNameModal from "../../components/EditGroupNameModal";
import EditGroupDescriptionModal from "../../components/EditGroupDescriptionModal";
import ChangeGroupPhotoModal from "../../components/ChangeGroupPhotoModal";
import GroupInfoModal from "../../components/GroupInfoModal";
import LeaveGroupModal from "../../components/LeaveGroupModal";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getGroups } from "../../services/groupService";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  searchField: {
    flex: 1,
    minWidth: 250,
  },
  filterField: {
    minWidth: 200,
  },
  groupCard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  groupAvatar: {
    width: theme.spacing(8),
    height: theme.spacing(8),
    marginBottom: theme.spacing(2),
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  groupInfo: {
    flex: 1,
  },
  groupName: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  groupMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  cardActions: {
    marginTop: "auto",
    justifyContent: "flex-end",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
  },
  emptyIcon: {
    fontSize: 80,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
}));

const Groups = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");
  const [connections, setConnections] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editNameModalOpen, setEditNameModalOpen] = useState(false);
  const [editDescriptionModalOpen, setEditDescriptionModalOpen] = useState(false);
  const [changePhotoModalOpen, setChangePhotoModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const data = await getGroups();
      setGroups(data.groups || []);
      
      // Extract unique connections
      const uniqueConnections = [...new Set(
        (data.groups || []).map(g => g.whatsappName)
      )].filter(Boolean);
      setConnections(uniqueConnections);
    } catch (err) {
      toastError(err);
      toast.error(i18n.t("groupManagement.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, group) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroup(group);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedGroup(null);
  };

  const handleViewInfo = () => {
    setInfoModalOpen(true);
    handleMenuClose();
  };

  const handleEditName = () => {
    setEditNameModalOpen(true);
    handleMenuClose();
  };

  const handleEditDescription = () => {
    setEditDescriptionModalOpen(true);
    handleMenuClose();
  };

  const handleChangePhoto = () => {
    setChangePhotoModalOpen(true);
    handleMenuClose();
  };

  const handleLeaveGroup = () => {
    setLeaveModalOpen(true);
    handleMenuClose();
  };

  const handleCreateGroup = () => {
    setCreateModalOpen(true);
  };

  const filteredGroups = groups.filter((group) => {
    const matchesSearch = group.name
      .toLowerCase()
      .includes(searchParam.toLowerCase());
    const matchesConnection =
      !selectedConnection || group.whatsappName === selectedConnection;
    return matchesSearch && matchesConnection;
  });

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("groupManagement.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateGroup}
          >
            {i18n.t("groupManagement.buttons.createGroup")}
          </Button>
          <IconButton onClick={loadGroups} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.searchContainer}>
          <TextField
            className={classes.searchField}
            placeholder={i18n.t("groupManagement.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl className={classes.filterField} variant="outlined">
            <InputLabel>
              {i18n.t("groupManagement.filterByConnection")}
            </InputLabel>
            <Select
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
              label={i18n.t("groupManagement.filterByConnection")}
            >
              <MenuItem value="">
                {i18n.t("groupManagement.allConnections")}
              </MenuItem>
              {connections.map((conn) => (
                <MenuItem key={conn} value={conn}>
                  {conn}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className={classes.emptyState}>
            <GroupIcon className={classes.emptyIcon} />
            <Typography variant="h6" color="textSecondary">
              {i18n.t("groupManagement.noGroups")}
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3}>
            {filteredGroups.map((group) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={group.id}>
                <Card className={classes.groupCard}>
                  <CardContent>
                    <div className={classes.groupHeader}>
                      <Avatar
                        src={group.pictureUrl}
                        className={classes.groupAvatar}
                      >
                        <GroupIcon />
                      </Avatar>
                    </div>
                    <div className={classes.groupInfo}>
                      <Typography
                        variant="h6"
                        className={classes.groupName}
                        noWrap
                      >
                        {group.name}
                      </Typography>
                      <div className={classes.groupMeta}>
                        <GroupIcon fontSize="small" />
                        <Typography variant="body2">
                          {group.participantCount || 0}{" "}
                          {i18n.t("groupManagement.table.participants")}
                        </Typography>
                      </div>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginTop: 8 }}
                      >
                        {group.whatsappName}
                      </Typography>
                    </div>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, group)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleViewInfo}>
            {i18n.t("groupManagement.actions.viewInfo")}
          </MenuItem>
          <MenuItem onClick={handleEditName}>
            {i18n.t("groupManagement.actions.editName")}
          </MenuItem>
          <MenuItem onClick={handleEditDescription}>
            {i18n.t("groupManagement.actions.editDescription")}
          </MenuItem>
          <MenuItem onClick={handleChangePhoto}>
            {i18n.t("groupManagement.actions.changePhoto")}
          </MenuItem>
          <MenuItem onClick={handleLeaveGroup}>
            {i18n.t("groupManagement.actions.leaveGroup")}
          </MenuItem>
        </Menu>

        {/* Modals */}
        <CreateGroupModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={loadGroups}
        />

        {selectedGroup && (
          <>
            <EditGroupNameModal
              open={editNameModalOpen}
              onClose={() => setEditNameModalOpen(false)}
              group={selectedGroup}
              onSuccess={loadGroups}
            />

            <EditGroupDescriptionModal
              open={editDescriptionModalOpen}
              onClose={() => setEditDescriptionModalOpen(false)}
              group={selectedGroup}
              onSuccess={loadGroups}
            />

            <ChangeGroupPhotoModal
              open={changePhotoModalOpen}
              onClose={() => setChangePhotoModalOpen(false)}
              group={selectedGroup}
              onSuccess={loadGroups}
            />

            <GroupInfoModal
              open={infoModalOpen}
              onClose={() => setInfoModalOpen(false)}
              group={selectedGroup}
            />

            <LeaveGroupModal
              open={leaveModalOpen}
              onClose={() => setLeaveModalOpen(false)}
              group={selectedGroup}
              onSuccess={loadGroups}
            />
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Groups;
